/**
 * Everything one campaign did, in seven tabs.
 *
 * The wizard answers "what shall this be?"; this answers "what is it, and what
 * happened?". Clicking a row lands here rather than back in the four-step form,
 * because a campaign is read far more often than it is rewritten.
 *
 * Some of what a mail platform normally shows here cannot be known from an
 * SMTP send: whether a message was opened, whether a link was clicked, whether
 * it bounced, and what came back. Each needs machinery this workspace does not
 * have yet — a tracking pixel and rewritten links need an address the
 * recipient's mail client can reach, and replies and bounces need something
 * reading a mailbox.
 *
 * So those tabs say what they would need rather than showing a number. An
 * invented open rate is worse than an empty one: it gets believed, and
 * campaigns get planned around it.
 */
import { useEffect, useMemo, useState } from 'react';
import { useApp } from '../../store/AppContext.jsx';
import Icon from '../../lib/icons.jsx';
import Sheet from '../Sheet.jsx';
import MailPreview from '../MailPreview.jsx';
import { linksIn, markHits, HEAT_CSS } from '../../lib/clickmap.js';
import { Avatar, Pill, Dash, EmptyState, Spinner } from '../Primitives.jsx';
import { download, digitsOf } from '../../lib/format.js';
import { describePhone } from '../../lib/phone.js';
import { channelLabel, channelIcon, channelPill } from '../../constants/marketing.js';
import {
  campaignStats, campaignRecipients, reachSummary, scheduleState, waStatus, waReplies, pushStats,
  applyMerge, isTimed, isOptedOut, campaignEngagement, campaignReplies
} from '../../domain/marketing.js';
import { looksHtml } from './CampaignEditor.jsx';
import { useInlineImages } from '../../lib/inlineImages.js';

const TABS = [
  ['overview', 'Overview', 'chart'],
  ['content', 'Content', 'mail'],
  ['log', 'Send log', 'list'],
  ['clicks', 'Click map', 'target'],
  ['audience', 'Audience', 'users'],
  ['replies', 'Responses', 'chat'],
  ['settings', 'Settings', 'gear']
];

const STATUS_TONE = {
  Sent: 'gr-green', Sending: 'gr-amber', Scheduled: 'gr-blue', Paused: 'gr-red'
};

const LOG_TONE = {
  sent: 'p-green', failed: 'p-red', unreachable: 'p-amber', skipped: 'p-grey', queued: 'p-blue',
  // WhatsApp's own ladder.
  accepted: 'p-blue', delivered: 'p-green', read: 'p-purple'
};

/** One figure. `need` marks a number nothing here can honestly produce yet. */
const Stat = ({ label, value, sub, tone, need }) => (
  <div className={`cvstat${tone ? ` ${tone}` : ''}${need ? ' need' : ''}`}>
    <small>{label}</small>
    <b>{need ? '—' : value}</b>
    <span>{need || sub || ''}</span>
  </div>
);

/** A labelled proportion bar. */
const Bar = ({ label, value, total, tone }) => {
  const pct = total ? Math.round((value / total) * 100) : 0;
  return (
    <div className="cvbar">
      <span className="cvbarlab">{label}</span>
      <div className="cvbartrack">
        <div className={`cvbarfill ${tone || ''}`} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
      <span className="cvbarval">{value.toLocaleString()} <em>({pct}%)</em></span>
    </div>
  );
};

/**
 * A tab that needs something the workspace does not have.
 *
 * It names the missing piece rather than apologising, because the reader's
 * next question is always "what would make this work".
 */
const Needs = ({ icon, title, why, what }) => (
  <div className="cvneed">
    <span className="ic g-slate"><Icon name={icon} /></span>
    <b>{title}</b>
    <p>{why}</p>
    <ul>{what.map(x => <li key={x}><Icon name="check" />{x}</li>)}</ul>
  </div>
);

export default function CampaignView({ id, onClose }) {
  const {
    mkt, mktCtx, fmtMoney, setSheet, saveCampaign, removeMarketingRecord,
    replies, loadReplies, checkReplies, events, loadEvents, tracking, toast
  } = useApp();

  const [checking, setChecking] = useState(false);

  const [tab, setTab] = useState('overview');

  /* The mailbox is read by the server; this only asks for what it already
     has, and only when somebody actually opens the tab. */
  useEffect(() => { if (tab === 'replies') loadReplies(); }, [tab, loadReplies]);
  useEffect(() => {
    if (tab === 'clicks' || tab === 'overview') loadEvents();
  }, [tab, loadEvents]);
  const [term, setTerm] = useState('');
  const [only, setOnly] = useState('all');

  const c = mkt.campaigns.find(x => x.id === id) || null;
  const isWa = !!c && c.channel === 'whatsapp';
  const isPush = !!c && c.channel === 'push';

  /* Hooks run whatever happens, so everything derived is computed before the
     early return rather than inside the branch that renders it. */
  const everyone = useMemo(() => (c ? campaignRecipients(c, mktCtx) : []), [c, mktCtx]);
  const summary = useMemo(
    () => (c ? reachSummary(everyone, c.channel, mktCtx) : { ok: [], notes: [] }),
    [c, everyone, mktCtx]
  );

  const byId = useMemo(
    () => new Map(((c && c.sends) || []).map(x => [x.rid, x])),
    [c]
  );

  const reachableIds = useMemo(() => new Set(summary.ok.map(r => r.id)), [summary.ok]);

  /*
   * One row per person, with what happened to them. Email reads the send
   * record kept in the campaign; WhatsApp reads the webhook log, where a
   * message climbs sent -> delivered -> read or lands on failed with a reason.
   */
  const waByRid = useMemo(
    () => (c && c.channel === 'whatsapp' ? waStatus(c, mktCtx).byRid : new Map()),
    [c, mktCtx]
  );
  const log = useMemo(() => everyone.map(r => {
    if (c && c.channel === 'whatsapp') {
      const m = waByRid.get(r.id);
      const s = byId.get(r.id);
      return {
        id: r.id,
        name: r.name,
        to: r.phone,
        status: m ? m.status : (s ? 'sent' : (reachableIds.has(r.id) ? 'queued' : 'unreachable')),
        at: m ? (m.status_at || m.at) : (s ? s.at : ''),
        error: m ? m.error : '',
        via: m ? 'WhatsApp' : (s ? s.via : '')
      };
    }
    const s = byId.get(r.id);
    return {
      id: r.id,
      name: r.name,
      to: c && c.channel === 'email' ? r.email : r.phone,
      status: s ? s.status : (reachableIds.has(r.id) ? 'queued' : 'unreachable'),
      at: s ? s.at : '',
      error: s ? s.error : '',
      via: s ? s.via : ''
    };
  }), [everyone, byId, reachableIds, c, mktCtx, waByRid]);

  const counts = useMemo(
    () => log.reduce((t, r) => ({ ...t, [r.status]: (t[r.status] || 0) + 1 }), {}),
    [log]
  );

  const shown = useMemo(() => {
    const q = term.trim().toLowerCase();
    return log.filter(r => {
      if (only !== 'all' && r.status !== only) return false;
      if (!q) return true;
      return [r.name, r.to].some(v => String(v || '').toLowerCase().includes(q));
    });
  }, [log, only, term]);

  const sample = summary.ok[0] || everyone[0] || null;
  const body = String((c && c.body) || '');
  const merged = useInlineImages(sample ? applyMerge(body, sample) : body);

  if (!c) {
    return (
      <Sheet icon="eye" title="Campaign" onClose={onClose} closeLabel="Close">
        <div style={{ gridColumn: '1/-1' }}>
          <EmptyState icon="alert" tone="g-red" title="That campaign is no longer here">
            It may have been deleted.
          </EmptyState>
        </div>
      </Sheet>
    );
  }

  const wa = isWa ? waStatus(c, mktCtx) : null;
  const push = isPush ? pushStats(c, mktCtx) : null;
  const stats = campaignStats(c, mktCtx);
  const seen = campaignEngagement(c, mktCtx);

  /*
   * The click map: the same counts twice over, once on the message and once
   * beside it. The label comes from the copy, because "Book a Free Demo" is
   * what somebody pressed and the address it points at is not — and a link
   * the copy no longer contains still appears, under the address, rather than
   * quietly dropping a click that really happened.
   */
  const hits = new Map(seen.links.map(l => [l.key, l.clicks]));
  const marked = markHits(merged, hits);

  const inCopy = linksIn(merged);
  const named = new Map(inCopy.map(l => [l.key, l]));

  const ranked = seen.links.map(l => {
    const found = named.get(l.key);
    return {
      ...l,
      label: (found && found.label) || l.url.replace(/^https?:\/\//, ''),
      image: !!(found && found.image),
      /*
       * A click on a link the copy no longer contains. It is still a real
       * click and still listed, but it cannot be drawn on the message — and
       * saying so is better than showing the address as though it were the
       * words somebody pressed.
       */
      gone: !found
    };
  });

  /*
   * The conversation, in the order it arrived. Only people this campaign was
   * actually sent to — the same mailbox carries every other kind of mail, and
   * a newsletter from a supplier is not a reply to your campaign.
   */
  const sentAddresses = new Map();
  everyone.forEach(r => {
    const s = byId.get(r.id);
    if (!s || s.status !== 'sent') return;
    const address = String(r.email || '').trim().toLowerCase();
    if (address) sentAddresses.set(address, { name: r.name, at: s.sentAt || s.at });
  });

  /*
   * This campaign's share of the mailbox, worked out in one place for every
   * screen that shows it — see replyOwners. An answer names the message it is
   * answering, so a second campaign to somebody who replied to the first no
   * longer shows the first one's reply as its own.
   */
  const conversation = campaignReplies(c, mktCtx).map(r => {
    const sent = sentAddresses.get(String(r.from_email || '').trim().toLowerCase());
    return { ...r, who: (sent && sent.name) || r.from_name || r.from_email };
  });

  const answered = new Set(conversation.filter(r => r.kind === 'reply').map(r => r.from_email));
  const bounced = conversation.filter(r => r.kind === 'bounce');

  const check = async () => {
    setChecking(true);
    try {
      const out = await checkReplies();
      toast(out.found
        ? `Read ${out.found} new message${out.found === 1 ? '' : 's'}`
        : 'Nothing new in the mailbox');
    } catch (e) {
      toast(e.message);
    }
    setChecking(false);
  };
  const due = scheduleState(c);
  const accepted = counts.sent || 0;
  const failed = counts.failed || 0;
  const optedOut = everyone.filter(r => isOptedOut(mkt, r)).length;
  const rich = c.channel === 'email' && looksHtml(merged);

  const duplicate = () => {
    const copy = { ...c };
    delete copy.id;
    const written = saveCampaign(null, {
      ...copy, name: `${c.name} (copy)`, status: 'Draft', schedule: '', sentAt: '', sends: []
    });
    toast('Copied — the copy is a draft');
    setSheet({ kind: 'campaignView', id: written });
  };

  const remove = () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete "${c.name}"? Its send history goes with it.`)) return;
    removeMarketingRecord('campaigns', c.id);
    onClose();
  };

  const exportLog = () => {
    const q = v => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
    const head = ['Name', 'Address', 'Status', 'Sent at', 'Through', 'Why not'];
    const lines = [head.map(q).join(',')].concat(shown.map(r =>
      [r.name, r.to, r.status, r.at, r.via, r.error].map(q).join(',')));
    download(
      new Blob([`﻿${lines.join('\r\n')}`], { type: 'text/csv;charset=utf-8' }),
      `send-log-${c.name.replace(/\W+/g, '-').toLowerCase()}.csv`
    );
    toast(`Exported ${shown.length} row${shown.length === 1 ? '' : 's'}`);
  };

  const hero = (
    <div className="cvhero">
      <span className={`ic ${channelPill(c.channel).replace('p-', 'g-')}`}>
        <Icon name={channelIcon(c.channel)} />
      </span>
      <div className="cvwho">
        <b>{c.name || 'Untitled campaign'}</b>
        <span>
          {channelLabel(c.channel)}
          {c.goal ? ` · ${c.goal.toLowerCase()}` : ''}
          {c.created ? ` · ${c.created}` : ''}
        </span>
      </div>
      <Pill tone={STATUS_TONE[c.status] || 'gr-slate'}>{c.status}</Pill>
    </div>
  );

  return (
    <Sheet
      icon="eye"
      tone="g-pink"
      title="Campaign"
      onClose={onClose}
      closeLabel="Close"
      bodyClass="cvbody"
      wide
      hero={hero}
      footerLeft={
        <>
          <button
            type="button"
            className="btn primary"
            onClick={() => setSheet({ kind: 'send', id: c.id })}
          >
            <Icon name="send" />{accepted ? 'Send again' : 'Send'}
          </button>
          <button type="button" className="btn" onClick={() => setSheet({ kind: 'campaign', id: c.id })}>
            <Icon name="edit" />Edit
          </button>
          <button type="button" className="btn" onClick={duplicate}>
            <Icon name="file" />Duplicate
          </button>
        </>
      }
    >
      <div className="cvwrap">
        <nav className="cvtabs">
          {TABS
            .filter(([key]) => !((isWa || isPush) && key === 'clicks'))
            .filter(([key]) => !(isPush && key === 'replies'))
            .map(([key, label, icon]) => (
            <button
              type="button"
              key={key}
              className={tab === key ? 'on' : ''}
              onClick={() => setTab(key)}
            >
              <Icon name={icon} />{label}
            </button>
          ))}
        </nav>

        {/* ------------------------------------------------------- overview */}
        {tab === 'overview' ? (
          <>
            <div className="cvstats">
              <Stat label="Audience" value={everyone.length} sub="targeted" />
              <Stat label="Reachable" value={summary.ok.length} sub="can be messaged" tone="blue" />
              {isPush ? (
                <>
                  <Stat label="Sent" value={push.sent} sub="accepted by Firebase" tone="green" />
                  <Stat
                    label="Failed"
                    value={push.failed}
                    sub={push.failed ? 'FCM refused' : 'none refused'}
                    tone={push.failed ? 'red' : ''}
                  />
                  <Stat label="Topic" value={push.topics[0] || '—'} sub="fanned out to devices" />
                </>
              ) : isWa ? (
                <>
                  <Stat label="Sent" value={wa.sent} sub="handed to WhatsApp" tone="green" />
                  <Stat
                    label="Delivered"
                    value={wa.delivered}
                    sub={wa.deliveryRate === null ? 'none yet' : `${wa.deliveryRate}% of sent`}
                    tone="green"
                  />
                  <Stat
                    label="Read"
                    value={wa.read}
                    sub={wa.readRate === null ? 'none yet' : `${wa.readRate}% of sent`}
                    tone="purple"
                  />
                  <Stat
                    label="Failed"
                    value={wa.failed}
                    sub={wa.failed ? 'WhatsApp refused' : 'none refused'}
                    tone={wa.failed ? 'red' : ''}
                  />
                </>
              ) : (
              <>
              <Stat label="Sent" value={accepted} sub="accepted by the server" tone="green" />
              <Stat
                label="Failed"
                value={failed}
                sub={failed ? 'refused' : 'none refused'}
                tone={failed ? 'red' : ''}
              />
              <Stat label="Opted out" value={optedOut} sub="on the opt-out list" />
              {tracking ? (
                <>
                  <Stat
                    label="Open rate"
                    value={seen.openRate === null ? '—' : `${seen.openRate}%`}
                    sub={seen.openedByClickOnly
                      ? `${seen.opened} read, ${seen.openedByClickOnly} of them by clicking`
                      : `${seen.opened} read`}
                    tone="purple"
                  />
                  <Stat
                    label="Click rate"
                    value={seen.clickRate === null ? '—' : `${seen.clickRate}%`}
                    sub={`${seen.clicked} clicked${seen.clicks > seen.clicked ? `, ${seen.clicks} times` : ''}`}
                    tone="blue"
                  />
                </>
              ) : (
                <>
                  <Stat label="Open rate" need="needs PUBLIC_URL set" />
                  <Stat label="Click rate" need="needs PUBLIC_URL set" />
                </>
              )}
              <Stat
                label="Bounced"
                value={bounced.length}
                sub={bounced.length ? 'address refused it' : 'none came back'}
                tone={bounced.length ? 'red' : ''}
              />
              </>
              )}
            </div>

            {due || summary.notes.length ? (
              <div className="cvflags">
                {due ? (
                  <Pill
                    tone={due.tone === 'over' ? 'gr-red' : due.tone === 'due' ? 'gr-amber' : 'gr-blue'}
                    icon="clock"
                  >
                    {`${c.schedule} · ${due.label}`}
                  </Pill>
                ) : null}
                {summary.notes.map(n => <Pill key={n} tone="gr-slate" icon="flag">{`skipped: ${n}`}</Pill>)}
              </div>
            ) : null}

            {isPush ? (
              <section className="cvpanel">
                <h4>Delivery</h4>
                <Bar label="Accepted" value={push.sent} total={(push.sent + push.failed) || 1} tone="green" />
                {push.failed ? <Bar label="Failed" value={push.failed} total={(push.sent + push.failed) || 1} tone="red" /> : null}
                <p className="cvnote">
                  Firebase confirms it accepted the notification for the topic and handed it
                  towards the devices. It cannot say how many phones showed it, and there is no
                  read receipt — tapping is the only real signal, and only if the app reports it.
                </p>
              </section>
            ) : isWa ? (
              <section className="cvpanel">
                <h4>Delivery</h4>
                <Bar label="Sent" value={wa.sent} total={wa.sent || 1} tone="slate" />
                <Bar label="Delivered" value={wa.delivered} total={wa.sent || 1} tone="green" />
                <Bar label="Read" value={wa.read} total={wa.sent || 1} tone="blue" />
                {wa.failed ? <Bar label="Failed" value={wa.failed} total={wa.sent || 1} tone="red" /> : null}
                <p className="cvnote">
                  These are real. WhatsApp confirms delivery to the phone, and a read receipt
                  comes from the phone itself — not a guess. A failure carries Meta’s own reason,
                  shown against each one in the send log.
                </p>
              </section>
            ) : (
            <section className="cvpanel">
              <h4>Delivery</h4>
              <Bar label="Targeted" value={everyone.length} total={everyone.length} tone="slate" />
              <Bar label="Reachable" value={summary.ok.length} total={everyone.length} tone="blue" />
              <Bar label="Sent" value={accepted} total={everyone.length} tone="green" />
              {failed ? <Bar label="Refused" value={failed} total={everyone.length} tone="red" /> : null}
              <p className="cvnote">
                “Sent” means the mail server accepted the message. Whether it reached the inbox,
                and whether anybody opened it, needs tracking this workspace does not have yet —
                the Click map tab says what that takes.
              </p>
            </section>
            )}

            <section className="cvpanel">
              <h4>What happened afterwards</h4>
              <div className="cvrows">
                <div><span>Replied</span><b>{stats.replied || <Dash />}</b></div>
                <div><span>Converted</span><b>{stats.converted || <Dash />}</b></div>
                <div><span>Revenue after</span><b>{stats.revenue ? fmtMoney(stats.revenue) : <Dash />}</b></div>
              </div>
              <p className="cvnote">
                Read from the client ledger rather than from the mail: somebody contacted after
                the send counts as a reply, a payment after it counts as a conversion. Marketing
                contacts are not on the ledger, so these stay empty for a campaign sent only to
                them.
              </p>
            </section>
          </>
        ) : null}

        {/* -------------------------------------------------------- content */}
        {tab === 'content' && isPush ? (
          <section className="cvpanel">
            <h4>The notification</h4>
            <div className="pushprev">
              <div className="pushnotif">
                <div className="pushicon"><Icon name="bell" /></div>
                <div className="pushbodywrap">
                  <div className="pushhead"><b>XYRA</b><span>now</span></div>
                  <div className="pushtitle">{c.pushTitle || 'No title'}</div>
                  <div className="pushtext">{c.pushBody || ''}</div>
                </div>
                {c.pushImage ? <img className="pushimg" src={c.pushImage} alt="" /> : null}
              </div>
            </div>
            <div className="cvrows wide" style={{ marginTop: 14 }}>
              <div><span>Topic</span><b>{c.pushTopic || (push && push.topics[0]) || '—'}</b></div>
              <div><span>Link when tapped</span><b>{c.pushLink || <Dash />}</b></div>
            </div>
          </section>
        ) : null}

        {tab === 'content' && !isPush ? (
          <section className="cvpanel">
            <h4>Message preview</h4>

            {c.channel === 'email' ? (
              <div className="cvsubject">
                <span>Subject</span>
                <b>{c.subject ? applyMerge(c.subject, sample || {}) : 'no subject'}</b>
              </div>
            ) : null}

            {body ? (
              <div className="cvmail">
                {rich
                  ? <MailPreview html={merged} minHeight={180} />
                  : <div className="cpbody">{merged}</div>}
              </div>
            ) : <p className="muted">Nothing written yet.</p>}

            <p className="cvnote">
              {sample
                ? `Merge fields are filled from ${sample.name}, the first person this reaches.`
                : 'No recipient to fill the merge fields from yet.'}
            </p>
          </section>
        ) : null}

        {/* ------------------------------------------------------- send log */}
        {tab === 'log' && isPush ? (
          <section className="cvpanel">
            {push.rows.length ? (
              <div className="tscroll">
                <table>
                  <thead><tr><th>To</th><th>Status</th><th>When</th></tr></thead>
                  <tbody>
                    {push.rows.map(r => (
                      <tr key={r.id}>
                        <td>{r.target === 'topic' ? `topic: ${r.topic}` : `device …${String(r.token || '').slice(-6)}`}</td>
                        <td>
                          <Pill tone={r.status === 'failed' ? 'p-red' : 'p-green'}>{r.status}</Pill>
                          {r.error ? <div className="cverr">{r.error}</div> : null}
                        </td>
                        <td className="dim">{r.at ? new Date(r.at).toLocaleString() : <Dash />}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <EmptyState icon="bell" tone="g-slate" title="Nothing sent yet">
                When this push goes out, each send Firebase accepts or refuses is listed here.
              </EmptyState>
            )}
          </section>
        ) : null}

        {tab === 'log' && !isPush ? (
          <section className="cvpanel">
            <div className="cvlogbar">
              {(isWa
                ? [
                  ['all', 'All', log.length],
                  ['delivered', 'Delivered', counts.delivered || 0],
                  ['read', 'Read', counts.read || 0],
                  ['sent', 'Sent, not yet delivered', counts.sent || 0],
                  ['failed', 'Failed', counts.failed || 0],
                  ['queued', 'Not sent yet', counts.queued || 0]
                ]
                : [
                  ['all', 'All', log.length],
                  ['sent', 'Sent', accepted],
                  ['failed', 'Failed', failed],
                  ['queued', 'Not sent yet', counts.queued || 0],
                  ['unreachable', 'Unreachable', counts.unreachable || 0],
                  ['skipped', 'Skipped', counts.skipped || 0]
                ]
              ).map(([key, label, n]) => (
                <button
                  type="button"
                  key={key}
                  className={`cvchip${only === key ? ' on' : ''}`}
                  onClick={() => setOnly(key)}
                  disabled={key !== 'all' && !n}
                >
                  {label}<em>{n}</em>
                </button>
              ))}
            </div>

            <div className="crow">
              <input
                className="csearch"
                value={term}
                placeholder="Search name or address…"
                onChange={e => setTerm(e.target.value)}
              />
              <button type="button" className="btn sm" onClick={exportLog} disabled={!shown.length}>
                <Icon name="down" />Export {shown.length}
              </button>
            </div>

            {shown.length ? (
              <>
                <div className="tscroll">
                  <table>
                    <thead>
                      <tr>
                        <th>Contact</th><th>{isWa ? 'Number' : 'Address'}</th><th>Status</th>
                        {!isWa && tracking ? <th>Opened</th> : null}
                        {!isWa && tracking ? <th>Clicked</th> : null}
                        <th>{isWa ? 'When' : 'Sent'}</th><th>Through</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shown.slice(0, 500).map(r => (
                        <tr key={r.id}>
                          <td>
                            <div className="cname">
                              <Avatar name={r.name} />
                              <div><b>{r.name}</b></div>
                            </div>
                          </td>
                          <td className="dim">{r.to || <Dash />}</td>
                          <td>
                            <Pill tone={LOG_TONE[r.status] || 'p-grey'}>{r.status}</Pill>
                            {r.error ? <div className="cverr">{r.error}</div> : null}
                          </td>
                          {!isWa && tracking ? (
                            <td>{seen.openedBy.has(r.id) ? <Pill tone="p-purple">opened</Pill> : <Dash />}</td>
                          ) : null}
                          {!isWa && tracking ? (
                            <td>{seen.clickedBy.has(r.id) ? <Pill tone="p-blue">clicked</Pill> : <Dash />}</td>
                          ) : null}
                          <td className="dim">{r.at || <Dash />}</td>
                          <td className="dim">{r.via || <Dash />}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {shown.length > 500 ? (
                  <p className="cvnote">
                    Showing the first 500 of {shown.length} — search to narrow it, or export
                    the lot.
                  </p>
                ) : null}
              </>
            ) : <p className="muted">Nothing matches that filter.</p>}
          </section>
        ) : null}

        {/* ------------------------------------------------------ click map */}
        {tab === 'clicks' ? (
          !tracking ? (
            <Needs
              icon="target"
              title="Tracking is not switched on"
              why={'Knowing somebody opened a message means putting a tiny image in it that their '
                + 'mail app loads from this server; knowing they clicked means routing every link '
                + 'through it first. Both need an address a stranger’s mail client can reach.'}
              what={[
                'Set PUBLIC_URL in backend/.env to the address this app answers on',
                'Restart the API',
                'Send a campaign — the pixel and the links go in as it sends',
                'Anything sent before that stays untracked; it cannot be added afterwards'
              ]}
            />
          ) : (
            <section className="cvpanel">
              <div className="cvstats" style={{ padding: '0 0 4px' }}>
                <Stat label="Read" value={seen.opened} sub={`of ${seen.sent} sent`} tone="purple" />
                <Stat
                  label="Images loaded"
                  value={seen.opens}
                  sub={seen.openedByClickOnly
                    ? `${seen.openedByClickOnly} read without them`
                    : 'including repeats'}
                />
                <Stat label="Clicked" value={seen.clicked} sub={`of ${seen.sent} sent`} tone="blue" />
                <Stat label="Clicks" value={seen.clicks} sub="including repeats" />
              </div>

              {seen.clicks ? (
                <>
                  <div className="cvheathead">
                    <div>
                      <h4>Where they clicked</h4>
                      <p>
                        The message as it was sent, with every link that earned a click
                        marked on it.
                      </p>
                    </div>
                    <div className="cvheattotal">
                      <b>{seen.clicks}</b>
                      <span>total clicks</span>
                    </div>
                  </div>

                  <div className="cvheat">
                    <div className="cvheatmail">
                      {body
                        ? <MailPreview html={marked} extraCss={HEAT_CSS} minHeight={260} />
                        : (
                          <p className="cvnote" style={{ padding: 16 }}>
                            The copy is no longer here, so there is nothing to mark up — the
                            ranking beside this is still the real count.
                          </p>
                        )}
                    </div>

                    <aside className="cvheatlinks">
                      <h5>Links by clicks</h5>

                      {ranked.map((l, i) => {
                        const share = seen.clicks
                          ? Math.round((l.clicks / seen.clicks) * 100)
                          : 0;

                        return (
                          <article className="cvlink" key={l.key}>
                            <div className="cvlinktop">
                              <b>
                                <span className="cvlinkrank">{i + 1}</span>
                                {l.image ? <Icon name="file" /> : null}
                                <span className="cvlinkname">{l.label}</span>
                              </b>
                              <span className="cvlinkhits">
                                <b>{l.clicks}</b>
                                <i>
                                  {l.people} {l.people === 1 ? 'person' : 'people'}
                                </i>
                              </span>
                            </div>
                            <div className="cvbartrack">
                              <div
                                className="cvbarfill rose"
                                style={{ width: `${Math.max(share, 2)}%` }}
                              />
                            </div>
                            <small className="cvlinkurl">{l.url}</small>
                            {l.gone ? (
                              <small className="cvlinkgone">
                                Not in the copy any more, so it is not marked on the message.
                              </small>
                            ) : null}
                          </article>
                        );
                      })}
                    </aside>
                  </div>
                </>
              ) : (
                <EmptyState icon="target" tone="g-slate" title="Nothing clicked yet">
                  Every link in the message is routed through this server, so a click shows up
                  here within seconds of somebody making it.
                </EmptyState>
              )}

              <p className="cvnote">
                A read is a floor, never a count. Knowing a message was opened means its
                pictures were loaded, and Gmail and Outlook block those by default — so anybody
                who read it with images off is invisible here unless they clicked. A click is
                counted as a read for exactly that reason: pressing a link is the firmer act of
                the two, and somebody who pressed one had certainly read the message.
              </p>
            </section>
          )
        ) : null}

        {/* ------------------------------------------------------- audience */}
        {tab === 'audience' ? (
          <>
            <section className="cvpanel">
              <h4>Who this reaches</h4>
              <Bar label="Targeted" value={everyone.length} total={everyone.length} tone="slate" />
              <Bar label="Can be reached" value={summary.ok.length} total={everyone.length} tone="blue" />
              <Bar label="Sent to" value={accepted} total={everyone.length} tone="green" />
              <Bar label="Refused" value={failed} total={everyone.length} tone="red" />
              <Bar label="Opted out" value={optedOut} total={everyone.length} tone="amber" />
            </section>

            <section className="cvpanel">
              <h4>Where they came from</h4>
              <div className="cvrows">
                <div>
                  <span>Saved segment</span>
                  <b>{(mkt.audiences.find(a => a.id === c.audience) || {}).name || <Dash />}</b>
                </div>
                <div><span>Named individually</span><b>{(c.contacts || []).length || <Dash />}</b></div>
                <div><span>With an email</span><b>{everyone.filter(r => r.email).length}</b></div>
                <div><span>With a phone</span><b>{everyone.filter(r => digitsOf(r.phone)).length}</b></div>
              </div>
              {summary.notes.length
                ? <p className="cvnote">Skipped: {summary.notes.join(', ')}.</p>
                : null}
            </section>
          </>
        ) : null}

        {/* ------------------------------------------------------ responses */}
        {tab === 'replies' && isWa ? (
          <section className="cvpanel">
            {(() => {
              const wr = waReplies(c, mktCtx);
              return wr.length ? (
                <div className="cvthread">
                  {wr.map(m => {
                    const ph = describePhone(m.phone);
                    return (
                    <article className="cvmsg reply" key={m.id}>
                      <header>
                        <Avatar name={m.name || ph.text} />
                        <div>
                          <b>{m.name || ph.text}</b>
                          <small className="cpphone">
                            {ph.flag ? <span className="cpflag">{ph.flag}</span> : null}
                            {m.name ? `${ph.text} · ` : ''}WhatsApp
                          </small>
                        </div>
                        <Pill tone="p-green">replied</Pill>
                        <time>{new Date(m.at).toLocaleString()}</time>
                      </header>
                      {m.body ? <p>{m.body}</p> : null}
                    </article>
                    );
                  })}
                </div>
              ) : (
                <EmptyState icon="chat" tone="g-slate" title="Nothing back yet">
                  When somebody replies to this campaign on WhatsApp it appears here, and they
                  can be answered from Chatlio&rsquo;s inbox.
                </EmptyState>
              );
            })()}
            <p className="cvnote">
              A reply is matched to the last campaign that reached its number before it arrived,
              so an older answer is not claimed by a newer send. Replying within 24 hours is
              free-form; after that WhatsApp needs a template again.
            </p>
          </section>
        ) : null}

        {tab === 'replies' && !isWa ? (
          <section className="cvpanel">
            <div className="cvlogbar" style={{ justifyContent: 'space-between' }}>
              <div className="cvlogbar" style={{ margin: 0 }}>
                <span className="cvchip on">Replies<em>{answered.size}</em></span>
                <span className="cvchip">Bounces<em>{bounced.length}</em></span>
              </div>
              <button type="button" className="btn sm" onClick={check} disabled={checking}>
                {checking ? <><Spinner />Reading…</> : <><Icon name="refresh" />Check now</>}
              </button>
            </div>

            {conversation.length ? (
              <div className="cvthread">
                {conversation.map(r => (
                  <article className={`cvmsg ${r.kind}`} key={r.id}>
                    <header>
                      <Avatar name={r.who || r.from_name || r.from_email} />
                      <div>
                        <b>{r.who || r.from_name || r.from_email}</b>
                        <small>{r.from_email}</small>
                      </div>
                      <Pill tone={r.kind === 'bounce' ? 'p-red' : r.kind === 'auto' ? 'p-grey' : 'p-green'}>
                        {r.kind === 'bounce' ? 'bounced' : r.kind === 'auto' ? 'auto-reply' : 'replied'}
                      </Pill>
                      <time>{new Date(r.received_at).toLocaleString()}</time>
                    </header>
                    {r.subject ? <b className="cvsub">{r.subject}</b> : null}
                    {r.snippet ? <p>{r.snippet}</p> : null}
                  </article>
                ))}
              </div>
            ) : (
              <EmptyState icon="chat" tone="g-slate" title="Nothing back yet">
                Replies to this campaign appear here as they arrive. The mailbox is read every
                five minutes — press “Check now” if you do not want to wait.
              </EmptyState>
            )}

            <p className="cvnote">
              Matched by the message being answered, which every reply names — so a later
              campaign to the same person cannot show this one&rsquo;s replies as its own.
              Where a reply names nothing, the quoted subject is used, and the address only
              after that. Somebody answering from a different address than the one you wrote
              to is not matched at all, and an out-of-office is marked as such rather than
              counted as a reply.
            </p>
          </section>
        ) : null}

        {/* ------------------------------------------------------- settings */}
        {tab === 'settings' ? (
          <section className="cvpanel">
            <h4>This campaign</h4>
            <div className="cvrows wide">
              <div><span>Campaign ID</span><b className="mono">{c.id}</b></div>
              <div><span>Channel</span><b>{channelLabel(c.channel)}</b></div>
              <div><span>Goal</span><b>{c.goal || <Dash />}</b></div>
              <div><span>Status</span><b>{c.status}</b></div>
              <div><span>Created</span><b>{c.created || <Dash />}</b></div>
              <div>
                <span>Scheduled</span>
                <b>{c.schedule ? `${c.schedule}${isTimed(c.schedule) ? '' : ' (date only)'}` : <Dash />}</b>
              </div>
              <div><span>First sent</span><b>{c.sentAt || <Dash />}</b></div>
              <div>
                <span>Sends from</span>
                <b>{c.accountId ? 'a chosen mailbox' : 'the first mail account'}</b>
              </div>
              <div><span>Audience</span><b>{everyone.length}</b></div>
              <div><span>Sent</span><b>{accepted}</b></div>
              <div><span>Failed</span><b>{failed}</b></div>
            </div>

            <div className="rowb" style={{ marginTop: 16 }}>
              <button type="button" className="btn sm" onClick={exportLog}>
                <Icon name="down" />Export the send log
              </button>
              <button type="button" className="btn sm danger" onClick={remove}>
                <Icon name="trash" />Delete campaign
              </button>
            </div>
          </section>
        ) : null}
      </div>
    </Sheet>
  );
}
